import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, Star, Shield, Clock, Users, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MultiStepForm from "@/components/MultiStepForm";
import axios from "axios";
import { useLang, Hreflang } from "@/i18n/LanguageContext";
import { CATS, ALL_CATS, catPath } from "@/i18n/slugs";
import { FAMILIES } from "@/i18n/taxonomy";
import { getHomeTestimonials } from "@/i18n/testimonials";
import { TestimonialsCarousel } from "@/components/TestimonialsCarousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryImages = {
  toiture: "https://images.pexels.com/photos/13075346/pexels-photo-13075346.jpeg?auto=compress&cs=tinysrgb&w=600",
  renovation: "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=600&q=80",
  chassis: "https://images.unsplash.com/photo-1616843221271-a3447ca74df3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  isolation: "https://images.unsplash.com/photo-1607400201889-565b1ee75f8e?w=600&q=80",
  "salle-de-bain": "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600&q=80",
  terrasse: "https://images.unsplash.com/photo-1768527341242-62055b63260b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  piscine: "https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=600&q=80",
  chauffage: "https://images.pexels.com/photos/4819052/pexels-photo-4819052.jpeg?auto=compress&cs=tinysrgb&w=600",
  electricite: "https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg?auto=compress&cs=tinysrgb&w=600",
  plafonnage: "https://images.unsplash.com/photo-1768839725085-829e6ac7ac26?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  menuiserie: "https://images.unsplash.com/photo-1537233907675-e1207a765f91?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "amenagement-exterieur": "https://images.unsplash.com/photo-1761637823293-f96835ec5038?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "entreprise-generale": "https://images.pexels.com/photos/8470035/pexels-photo-8470035.jpeg?auto=compress&cs=tinysrgb&w=600",
  carrelage: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=600&q=80",
  cuisine: "https://images.unsplash.com/photo-1772567732969-c1506edf80a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "extension-maison": "https://images.pexels.com/photos/4692281/pexels-photo-4692281.jpeg?auto=compress&cs=tinysrgb&w=600",
  "pompe-a-chaleur": "https://images.pexels.com/photos/20046693/pexels-photo-20046693.jpeg?auto=compress&cs=tinysrgb&w=600",
  climatisation: "https://images.unsplash.com/photo-1762341123870-d706f257a12e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  plomberie: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=600",
  facade: "https://images.unsplash.com/photo-1632711057336-ddd25c1d921f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  parquet: "https://images.pexels.com/photos/15066939/pexels-photo-15066939.jpeg?auto=compress&cs=tinysrgb&w=600",
  peinture: "https://images.pexels.com/photos/5493654/pexels-photo-5493654.jpeg?auto=compress&cs=tinysrgb&w=600",
};

const advantageIcons = [Shield, Users, Star, Clock];

export default function HomePage() {
  const [publicReviews, setPublicReviews] = useState([]);
  const { lang, T } = useLang();
  const H = T.home;

  useEffect(() => {
    axios.get(`${API}/reviews/public?limit=6`).then(res => {
      setPublicReviews(res.data);
    }).catch(() => {});
  }, []);

  const displayReviews = publicReviews.map((r) => ({
    name: r.reviewer_name,
    city: (ALL_CATS.find((c) => c.key === r.project_type)?.[lang]?.name) || r.project_type || "",
    date: r.created_at ? new Date(r.created_at).toLocaleDateString(lang === "nl" ? "nl-BE" : "fr-BE", { month: "long", year: "numeric" }) : "",
    text: r.comment,
    rating: r.rating
  }));

  const canonical = lang === "nl" ? "https://devismaison.be/nl" : "https://devismaison.be/";

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Helmet>
        <title>{H.metaTitle}</title>
        <meta name="description" content={H.metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DevisMaison.be" />
        <meta property="og:title" content={H.ogTitle} />
        <meta property="og:description" content={H.ogDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://images.pexels.com/photos/13075346/pexels-photo-13075346.jpeg?auto=compress&cs=tinysrgb&w=800&h=420&fit=crop" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "DevisMaison.be",
          "url": "https://devismaison.be",
          "description": H.metaDesc,
          "areaServed": ["Belgique", "Luxembourg"],
          "priceRange": lang === "nl" ? "Gratis voor particulieren" : "Gratuit pour les particuliers",
          "email": "info@devismaison.be"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "DevisMaison.be",
          "url": "https://devismaison.be"
        })}</script>
        <meta property="og:locale" content={lang === "nl" ? "nl_BE" : "fr_BE"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={H.ogTitle} />
        <meta name="twitter:description" content={H.ogDesc} />
        <meta name="twitter:image" content="https://images.pexels.com/photos/13075346/pexels-photo-13075346.jpeg?auto=compress&cs=tinysrgb&w=1200" />
      </Helmet>
      <Hreflang frPath="/" nlPath="/nl" />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 hero-pattern" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Text - Centered */}
          <div className="text-center max-w-4xl mx-auto mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500 mb-4 animate-fade-in">
              {H.kicker}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6 animate-fade-in animate-delay-100">
              {H.h1a}{" "}
              <span className="text-orange-500">{H.h1b}</span>{" "}
              {H.h1c}
            </h1>
            <p className="text-lg text-slate-600 mb-8 animate-fade-in animate-delay-200 max-w-2xl mx-auto">
              {H.sub}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animate-delay-300">
              <a href="#lead-form">
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 rounded-full text-lg group transition-all duration-200 hover:shadow-xl hover:shadow-orange-500/25"
                  data-testid="hero-cta-button"
                >
                  {H.cta}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-6 mt-8 justify-center text-sm text-slate-500 animate-fade-in animate-delay-400">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>{H.free}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>{H.noCommit}</span>
              </div>
            </div>
          </div>

          {/* Form - Below */}
          <div
            id="lead-form"
            className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 lg:p-8 border border-slate-100"
            data-testid="lead-form-container"
          >
            <MultiStepForm />
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 lg:py-32 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-4">
              {H.howTitle}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {H.howSub}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {H.steps.map((step, index) => (
              <div key={step.number} className="relative text-center">
                <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-orange-500/25">
                  <span className="font-heading text-2xl font-bold text-white">{step.number}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-slate-600">{step.description}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-slate-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 lg:py-32" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-4">
              {H.catsTitle}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {H.catsSub}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {CATS.slice(0, 8).map((category) => (
              <a
                key={category.key}
                href={catPath(category.key, lang)}
                target="_blank"
                rel="noopener noreferrer"
                className="group category-card"
                data-testid={`category-${category.key}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={categoryImages[category.key]}
                    alt={category[lang].name}
                    className="category-card-image"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2 group-hover:text-orange-500 transition-colors">
                    {category[lang].name}
                  </h3>
                  <p className="text-slate-600 text-sm">{category[lang].desc}</p>
                  <div className="flex items-center gap-2 text-orange-500 font-medium text-sm mt-4">
                    <span>{H.askQuote}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </a>
            ))}
          </div>

          <h3 className="font-heading text-2xl font-semibold text-slate-900 text-center mb-8" data-testid="all-categories-title">
            {lang === "nl" ? `Alle ${ALL_CATS.length} soorten werken` : `Tous les ${ALL_CATS.length} types de travaux`}
          </h3>
          <div className="max-w-4xl mx-auto space-y-3">
            {FAMILIES.map((f) => {
              const cats = ALL_CATS.filter((c) => c.family === f.key);
              return (
                <details key={f.key} className="group bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`family-${f.key}`}>
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none hover:bg-slate-50 transition-colors">
                    <span className="flex items-center gap-3">
                      <span className="text-2xl">{f.icon}</span>
                      <span className="font-heading font-semibold text-slate-900">{lang === "nl" ? f.nl : f.fr}</span>
                      <span className="text-sm text-slate-400">({cats.length})</span>
                    </span>
                    <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-6 pb-5 flex flex-wrap gap-2">
                    {cats.map((cat) => (
                      <a
                        key={cat.key}
                        href={catPath(cat.key, lang)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-orange-100 hover:text-orange-700 rounded-full text-slate-700 transition-colors"
                        data-testid={`familycat-${cat.key}`}
                      >
                        {cat[lang].nav}
                      </a>
                    ))}
                  </div>
                </details>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to={catPath("renovation", lang)}>
              <Button variant="outline" className="gap-2">
                {H.seeAll}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 lg:py-32 bg-slate-900 text-white" data-testid="advantages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              {H.whyTitle}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {H.whySub}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {H.advantages.map((advantage, i) => {
              const Icon = advantageIcons[i];
              return (
                <div key={advantage.title} className="text-center">
                  <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2">{advantage.title}</h3>
                  <p className="text-slate-400 text-sm">{advantage.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel
        lang={lang}
        items={displayReviews.length >= 3 ? displayReviews : getHomeTestimonials(lang)}
        title={H.reviewsTitle}
        className="bg-white"
      />

      {/* CTA Section */}

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-orange-500" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
            {H.ctaTitle}
          </h2>
          <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
            {H.ctaSub}
          </p>
          <a href="#lead-form">
            <Button
              className="bg-white text-orange-500 hover:bg-orange-50 font-semibold px-8 py-6 rounded-full text-lg"
              data-testid="cta-bottom-button"
            >
              {H.ctaBtn}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
